'use client';

import { CalendarEvent } from '@/lib/types';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Clock, AlignLeft, Plus, Pencil, CalendarDays, Church, Cross } from 'lucide-react';
import { format, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { RichText } from '@/components/ui/rich-text';
import { hexToRgba, type EventStyle } from './Calendar';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface DayEventsDialogProps {
    day: Date;
    events: CalendarEvent[];
    isOpen: boolean;
    isEditable: boolean;
    /** Resuelve color/escudo del evento según su hermandad. */
    styleOf: (event: CalendarEvent) => EventStyle;
    onClose: () => void;
    /** Editar (modo admin) o simplemente inspeccionar un evento concreto. */
    onSelectEvent?: (event: CalendarEvent) => void;
    /** Crear un evento nuevo en este día (solo modo editable). */
    onCreate?: (day: Date) => void;
}

/** Horario del evento relativo al día que se está mirando. */
function eventSchedule(event: CalendarEvent, day: Date): string {
    if (event.allDay) return 'Todo el día';
    const start = new Date(event.start);
    const end = new Date(event.end);
    const startsToday = isSameDay(start, day);
    const endsToday = isSameDay(end, day);
    if (startsToday && endsToday) return `${format(start, 'HH:mm')} – ${format(end, 'HH:mm')}`;
    if (startsToday) return `Empieza a las ${format(start, 'HH:mm')}`;
    if (endsToday) return `Termina a las ${format(end, 'HH:mm')}`;
    return 'Todo el día (evento de varios días)';
}

/** Rango completo del evento, para los que abarcan varios días. */
function eventRange(event: CalendarEvent): string | null {
    const start = new Date(event.start);
    const end = new Date(event.end);
    if (isSameDay(start, end)) return null;
    return `${format(start, "d 'de' MMM", { locale: es })} → ${format(end, "d 'de' MMM", { locale: es })}`;
}

export function DayEventsDialog({
    day, events, isOpen, isEditable, styleOf, onClose, onSelectEvent, onCreate,
}: DayEventsDialogProps) {
    // Los eventos de todo el día primero; el resto por hora de inicio.
    const sorted = [...events].sort((a, b) => {
        if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
        return new Date(a.start).getTime() - new Date(b.start).getTime();
    });

    const procesiones = sorted.filter(e => e.tipo === 'procesion');
    const cultos = sorted.filter(e => e.tipo === 'cultos');
    // Las procesiones se muestran SOLO en la cabecera (con su horario); no se
    // repiten en "Eventos". Los cultos van en su propia sección.
    const eventos = sorted.filter(e => e.tipo !== 'cultos' && e.tipo !== 'procesion');

    return (
        <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="flex max-h-[90dvh] flex-col p-0 sm:max-w-2xl">
                <DialogHeader className="px-6 pt-6">
                    <div className="flex w-full min-w-0 flex-wrap items-start justify-between gap-4 overflow-hidden">
                        <DialogTitle className="flex min-w-0 items-center gap-3 text-2xl font-bold">
                            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-primary/30 bg-primary/15 text-primary">
                                <CalendarDays className="h-5 w-5" />
                            </span>
                            <span className="capitalize">
                                {format(day, "eeee, d 'de' MMMM", { locale: es })}
                                <span className="block text-sm font-normal text-muted-foreground">
                                    {sorted.length} {sorted.length === 1 ? 'evento programado' : 'eventos programados'}
                                </span>
                            </span>
                        </DialogTitle>

                        {/* Procesiones del día: nombre en grande con su color y, debajo,
                            su descripción. Clicable para poder editarlas. */}
                        {procesiones.length > 0 && (
                            <div className="flex w-full min-w-0 flex-col items-end gap-2 sm:w-auto sm:max-w-[55%]">
                                {procesiones.map((p) => {
                                    const st = styleOf(p);
                                    const c = st.color;
                                    return (
                                        <div
                                            key={p.id}
                                            role={onSelectEvent ? 'button' : undefined}
                                            onClick={onSelectEvent ? () => onSelectEvent(p) : undefined}
                                            className={cn(
                                                'flex min-w-0 items-start gap-2',
                                                onSelectEvent && 'cursor-pointer transition-opacity hover:opacity-80',
                                            )}
                                        >
                                            {st.escudoUrl
                                                ? <img src={st.escudoUrl} alt="" className="mt-1 h-10 w-10 shrink-0 object-contain" />
                                                : <Church className="mt-1 h-6 w-6 shrink-0" style={{ color: c }} />}
                                            <div className="min-w-0 text-right">
                                                {/* `break-words` + `line-clamp` para que un nombre
                                                    largo no se salga de la tarjeta. */}
                                                <p
                                                    className="line-clamp-2 break-words text-2xl font-extrabold leading-tight"
                                                    style={{ color: c }}
                                                    title={p.title}
                                                >
                                                    {p.title}
                                                </p>
                                                {/* Si es de todo el día no se escribe el horario:
                                                    se pasa directo a la descripción. */}
                                                {!p.allDay && (
                                                    <p className="mt-0.5 flex items-center justify-end gap-1.5 text-xs font-medium text-muted-foreground">
                                                        <Clock className="h-3.5 w-3.5" />
                                                        {eventSchedule(p, day)}
                                                    </p>
                                                )}
                                                {p.description && (
                                                    <RichText className="mt-1 text-right text-xs text-muted-foreground">
                                                        {p.description}
                                                    </RichText>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <DialogDescription className="sr-only">
                        Listado detallado de los eventos de este día.
                    </DialogDescription>
                </DialogHeader>

                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 pb-6 pt-2">
                    {sorted.length === 0 && (
                        <p className="rounded-2xl border border-dashed border-white/10 py-10 text-center text-sm text-muted-foreground">
                            No hay eventos este día.
                        </p>
                    )}

                    {eventos.length > 0 && <SectionHeading icon={<CalendarDays className="h-4 w-4" />} label="Eventos" count={eventos.length} />}

                    {eventos.map((event, i) => (
                        <EventCard
                            key={`${event.id}-e${i}`}
                            event={event} day={day} index={i} style={styleOf(event)}
                            isEditable={isEditable} onSelectEvent={onSelectEvent}
                        />
                    ))}

                    {cultos.length > 0 && (
                        <>
                            <SectionHeading icon={<Cross className="h-4 w-4" />} label="Cultos" count={cultos.length} tone="violet" />
                            {cultos.map((event, i) => (
                                <EventCard
                                    key={`${event.id}-c${i}`}
                                    event={event} day={day} index={i} isCulto style={styleOf(event)}
                                    isEditable={isEditable} onSelectEvent={onSelectEvent}
                                />
                            ))}
                        </>
                    )}

                    {isEditable && (
                        <Button
                            variant="outline"
                            className="w-full gap-2 rounded-xl border-dashed border-white/15"
                            onClick={() => onCreate?.(day)}
                        >
                            <Plus className="h-4 w-4" /> Añadir evento a este día
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

/** Encabezado de sección ("Eventos" / "Cultos") con contador. */
function SectionHeading({ icon, label, count, tone = 'primary' }: {
    icon: React.ReactNode; label: string; count: number; tone?: 'primary' | 'violet';
}) {
    return (
        <div className="flex items-center gap-2 pt-1">
            <span className={cn(
                'flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider',
                tone === 'violet' ? 'text-violet-300' : 'text-primary',
            )}>
                {icon} {label}
            </span>
            <span className={cn(
                'rounded-full px-2 py-0.5 text-[0.65rem] font-semibold',
                tone === 'violet' ? 'bg-violet-400/15 text-violet-300' : 'bg-primary/15 text-primary',
            )}>
                {count}
            </span>
            <span className={cn('h-px flex-1', tone === 'violet' ? 'bg-violet-300/25' : 'bg-white/10')} />
        </div>
    );
}

function EventCard({ event, day, index, isEditable, onSelectEvent, style, isCulto = false }: {
    event: CalendarEvent;
    day: Date;
    index: number;
    isEditable: boolean;
    onSelectEvent?: (event: CalendarEvent) => void;
    style: EventStyle;
    isCulto?: boolean;
}) {
    const color = style.color;
    const range = eventRange(event);

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(index * 0.04, 0.3) }}
            className={cn(
                'relative overflow-hidden rounded-2xl border p-4 transition-colors',
                // Los cultos toman el color de su hermandad (borde discontinuo + tinte).
                isCulto ? 'border-dashed' : 'border-white/8 bg-muted/30',
                onSelectEvent && 'cursor-pointer hover:brightness-110',
            )}
            style={isCulto ? {
                borderColor: hexToRgba(color, 0.55),
                backgroundColor: hexToRgba(color, 0.12),
            } : undefined}
            onClick={() => onSelectEvent?.(event)}
        >
            <span className="absolute inset-y-0 left-0 w-1.5" style={{ backgroundColor: color }} />
            <div className="flex items-start justify-between gap-3 pl-3">
                <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className={cn('text-base font-bold leading-tight', isCulto && 'italic')}>{event.title}</h3>
                        {event.tipo === 'procesion' && (
                            <span className="flex items-center gap-1 rounded-full border border-amber-400/50 bg-amber-400/15 px-2 py-0.5 text-[0.65rem] font-semibold text-amber-300">
                                {style.escudoUrl
                                    ? <img src={style.escudoUrl} alt="" className="h-5 w-5 object-contain" />
                                    : <Church className="h-3 w-3" />}
                                Procesión
                            </span>
                        )}
                        {style.hermandadNombre && (
                            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[0.65rem] text-muted-foreground">
                                {style.hermandadNombre}
                            </span>
                        )}
                        {isCulto && (
                            <span className="flex items-center gap-1 rounded-full border border-violet-400/50 bg-violet-400/15 px-2 py-0.5 text-[0.65rem] font-semibold text-violet-300">
                                {style.escudoUrl
                                    ? <img src={style.escudoUrl} alt="" className="h-5 w-5 object-contain" />
                                    : <Cross className="h-3 w-3" />}
                                Cultos
                            </span>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5" />
                            {eventSchedule(event, day)}
                        </span>
                        {range && (
                            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5">{range}</span>
                        )}
                    </div>

                    {event.description && (
                        <div className="flex items-start gap-2 text-sm text-muted-foreground">
                            <AlignLeft className="mt-0.5 h-4 w-4 shrink-0" />
                            <RichText className="prose-sm">{event.description}</RichText>
                        </div>
                    )}
                </div>

                {isEditable && (
                    <Button
                        size="icon" variant="ghost" className="h-8 w-8 shrink-0"
                        onClick={(e) => { e.stopPropagation(); onSelectEvent?.(event); }}
                    >
                        <Pencil className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </motion.div>
    );
}
